/**
 * Sales Team Report parameter form: the window mode toggle, the date input(s)
 * for the active mode, the location multi-select, and the Run button.
 *
 * Extracted from SalesTeamFunnelReportModal when range mode was added, so the
 * modal keeps only the status machine and layout.
 */

import React from 'react';
import { CalendarIcon, PlayIcon } from '@heroicons/react/24/outline';
import {
  FUNNEL_HISTORY_START,
  FUNNEL_MAX_RANGE_DAYS,
} from '../../../types/salesTeamFunnel';
import { formatLongDate, inclusiveDayCount, todayLocal } from '../../../utils/localDate';
import {
  ELIGIBLE_LOCATIONS,
  FunnelReportParams,
  enterRangeMode,
  selectedIds,
  setRangeEnd,
} from './salesTeamFunnelParams';

const MODES: { key: 'recent' | 'range'; label: string }[] = [
  { key: 'recent', label: 'Last 5 working days' },
  { key: 'range', label: 'Date range' },
];

const dateInputClass =
  'block pl-8 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm';

export const SalesTeamFunnelParamsForm: React.FC<{
  params: FunnelReportParams;
  onChange: (next: FunnelReportParams) => void;
  onRun: () => void;
  running: boolean;
  validation: { canRun: boolean; message: string | null };
  runningMessage: string;
}> = ({ params, onChange, onRun, running, validation, runningMessage }) => {
  const ids = selectedIds(params);
  const today = todayLocal();
  const invalidRange = !!validation.message && params.mode === 'range';

  const setMode = (mode: 'recent' | 'range') => {
    if (mode === params.mode) return;
    onChange(mode === 'range' ? enterRangeMode(params) : { ...params, mode: 'recent' });
  };

  const toggleLocation = (id: number) => {
    const next = new Set(params.locationIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange({ ...params, locationIds: next });
  };

  const spanDays = params.mode === 'range'
    && /^\d{4}-\d{2}-\d{2}$/.test(params.startDate)
    && /^\d{4}-\d{2}-\d{2}$/.test(params.endDate)
    && params.startDate <= params.endDate
    ? inclusiveDayCount(params.startDate, params.endDate)
    : null;

  return (
    <div className="mb-6 print:hidden">
      {/* Window mode. Buttons + ARIA radio roles rather than native radios,
          because the segmented look is the requirement. */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Report Window</label>
        <div
          role="radiogroup"
          aria-label="Report window"
          className="inline-flex rounded-md border border-gray-300 bg-gray-100 p-0.5"
        >
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              role="radio"
              aria-checked={params.mode === m.key}
              onClick={() => setMode(m.key)}
              disabled={running}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                params.mode === m.key
                  ? 'bg-white shadow-sm text-blue-700 font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              } ${running ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-6">
        {params.mode === 'recent' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Date</label>
            <div className="relative">
              <CalendarIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                aria-label="Report date"
                value={params.reportDate}
                max={today}
                onChange={(e) => onChange({ ...params, reportDate: e.target.value })}
                disabled={running}
                className={dateInputClass}
              />
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <div className="relative">
                <CalendarIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  aria-label="Start date"
                  value={params.startDate}
                  min={FUNNEL_HISTORY_START}
                  max={params.endDate || today}
                  onChange={(e) => onChange({ ...params, startDate: e.target.value })}
                  disabled={running}
                  className={`${dateInputClass}${invalidRange ? ' border-red-300' : ''}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <div className="relative">
                <CalendarIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  aria-label="End date"
                  value={params.endDate}
                  min={params.startDate || FUNNEL_HISTORY_START}
                  max={today}
                  onChange={(e) => onChange(setRangeEnd(params, e.target.value))}
                  disabled={running}
                  className={`${dateInputClass}${invalidRange ? ' border-red-300' : ''}`}
                />
              </div>
            </div>
          </>
        )}

        <button
          onClick={onRun}
          disabled={!validation.canRun || running}
          title={validation.message || undefined}
          className={`
            inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors
            ${!validation.canRun || running
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
            }
          `}
        >
          {running ? (
            <>
              <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {runningMessage}
            </>
          ) : (
            <>
              <PlayIcon className="h-4 w-4 mr-2" />
              Run Report
            </>
          )}
        </button>
      </div>

      {params.mode === 'range' && (
        <p className="mt-2 text-xs text-gray-500">
          Lead and visit history begins {formatLongDate(FUNNEL_HISTORY_START)}. Maximum
          range {FUNNEL_MAX_RANGE_DAYS} days
          {spanDays !== null && ` — currently ${spanDays}`}.
          Weeks are grouped by month (1st–7th, 8th–14th, …).
        </p>
      )}

      {validation.message && (
        <p role="alert" className="mt-2 text-sm text-red-600">{validation.message}</p>
      )}

      {/* Location multi-select */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            Locations ({ids.length} of {ELIGIBLE_LOCATIONS.length} selected)
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => onChange({ ...params, locationIds: new Set(ELIGIBLE_LOCATIONS.map(l => l.id)) })}
              disabled={running}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => onChange({ ...params, locationIds: new Set<number>() })}
              disabled={running}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
            >
              Clear All
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ELIGIBLE_LOCATIONS.map(loc => (
            <label
              key={loc.id}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer transition-colors
                ${params.locationIds.has(loc.id)
                  ? 'border-blue-300 bg-blue-50 text-blue-800'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }
                ${running ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input
                type="checkbox"
                checked={params.locationIds.has(loc.id)}
                onChange={() => toggleLocation(loc.id)}
                disabled={running}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="truncate">{loc.name}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};
